import { useCallback, useEffect, useState } from 'react';
import {
    Plus, Search, Loader2, Pencil, Trash2, Shield, Users,
    Briefcase, UserCheck, Mail, Phone, X, Eye, EyeOff, XCircle,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { useAuth, hasPermission } from '@/context/AuthContext';
import { toast } from 'sonner';
import PageHeader from '@/components/ux/PageHeader';
import { NativeSelect } from '@/components/ux/NativeSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import Pagination from '@/components/ui/Pagination';
import { ViewToggle, useViewMode } from '@/components/ux/ViewToggle';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 12;

// Base roles used for filter chips (always present)
const BASE_ROLES = [
    { value: 'all',         label: 'All Members' },
    { value: 'admin',       label: 'Admin' },
    { value: 'hr_manager',  label: 'HR Manager' },
    { value: 'interviewer', label: 'Interviewer' },
    { value: 'candidate',   label: 'Candidate' },
    { value: 'ml_engineer', label: 'ML Engineer' },
];

const ROLE_META = {
    admin:       { label: 'Admin',       Icon: Shield,    color: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/20', avatar: 'bg-violet-500' },
    hr_manager:  { label: 'HR Manager',  Icon: Briefcase, color: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/20',   avatar: 'bg-blue-500'   },
    interviewer: { label: 'Interviewer', Icon: UserCheck,  color: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/20',   avatar: 'bg-teal-500'   },
    candidate:   { label: 'Candidate',   Icon: Users,      color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/20', avatar: 'bg-amber-500'  },
    ml_engineer: { label: 'ML Engineer', Icon: Shield,    color: 'bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/20',   avatar: 'bg-pink-500'   },
};

function getInitials(first, last) {
    return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase() || '?';
}

function UserAvatar({ user, size = 'md' }) {
    const meta = ROLE_META[user.role] || ROLE_META.candidate;
    const sz = size === 'lg' ? 'h-14 w-14 text-lg'
        : size === 'sm' ? 'h-8 w-8 text-[11px]'
        : 'h-10 w-10 text-sm';
    return (
        <div className={cn('flex shrink-0 items-center justify-center rounded-full font-semibold text-white', meta.avatar, sz)}>
            {getInitials(user.firstName, user.lastName)}
        </div>
    );
}

function RoleBadge({ role }) {
    const meta = ROLE_META[role];
    if (!meta) return <Badge variant="outline" className="capitalize">{role}</Badge>;
    const { Icon, color, label } = meta;
    return (
        <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium', color)}>
            <Icon className="h-3 w-3" />
            {label}
        </span>
    );
}

function UserCard({ user, canEdit, canDelete, onEdit, onDelete, currentUserId }) {
    const isSelf = user._id === currentUserId;
    const showActions = !isSelf && user.role !== 'super_admin' && (canEdit || canDelete);
    return (
        <div className="group relative flex flex-col gap-3 rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-md">
            {/* Active status dot */}
            <span className={cn(
                'absolute right-3 top-3 h-2 w-2 rounded-full ring-2 ring-card',
                user.isActive ? 'bg-green-500' : 'bg-muted-foreground/40'
            )} title={user.isActive ? 'Active' : 'Inactive'} />

            <div className="flex items-start gap-3">
                <UserAvatar user={user} />
                <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-foreground leading-tight">
                        {user.firstName} {user.lastName}
                        {isSelf && <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">(you)</span>}
                    </p>
                    <RoleBadge role={user.role} />
                </div>
            </div>

            <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{user.email}</span>
                </div>
                {user.phone && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <span>{user.phone}</span>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between pt-1">
                <span className={cn('text-xs font-medium', user.isActive ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground')}>
                    {user.isActive ? 'Active' : 'Inactive'}
                </span>
                {showActions && (
                    <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        {canEdit && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onEdit(user)}
                            aria-label="Edit user"
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        )}
                        {canDelete && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => onDelete(user)}
                            aria-label="Delete user"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Dense row alternative to UserCard — same actions, built for scanning. */
function UserRow({ user, canEdit, canDelete, onEdit, onDelete, currentUserId }) {
    const isSelf = user._id === currentUserId;
    const showActions = !isSelf && user.role !== 'super_admin' && (canEdit || canDelete);

    return (
        <div className="group grid grid-cols-12 items-center gap-2 border-b border-border/60 px-3 py-2.5 transition-colors last:border-0 hover:bg-muted/40">
            <div className="col-span-12 flex min-w-0 items-center gap-2.5 sm:col-span-4">
                <UserAvatar user={user} size="sm" />
                <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-foreground">
                        {user.firstName} {user.lastName}
                        {isSelf && <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">(you)</span>}
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">{user.email}</p>
                </div>
            </div>

            <div className="col-span-6 sm:col-span-3">
                <RoleBadge role={user.role} />
            </div>

            <div className="col-span-6 hidden sm:col-span-2 sm:block">
                <span className="truncate text-[11px] text-muted-foreground">{user.phone || '—'}</span>
            </div>

            <div className="col-span-6 sm:col-span-2">
                <span className={cn(
                    'inline-flex items-center gap-1.5 text-[11px] font-medium',
                    user.isActive ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground',
                )}>
                    <span className={cn('h-1.5 w-1.5 rounded-full', user.isActive ? 'bg-green-500' : 'bg-muted-foreground/40')} />
                    {user.isActive ? 'Active' : 'Inactive'}
                </span>
            </div>

            <div className="col-span-6 flex items-center justify-end gap-0.5 sm:col-span-1">
                {showActions && (
                    <>
                        {canEdit && (
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                                onClick={() => onEdit(user)} aria-label="Edit user">
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                        )}
                        {canDelete && (
                            <Button variant="ghost" size="icon"
                                className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => onDelete(user)} aria-label="Delete user">
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

const EMPTY_FORM = { firstName: '', lastName: '', email: '', password: '', phone: '', role: 'hr_manager' };
const EMPTY_EDIT = { firstName: '', lastName: '', email: '', phone: '', role: '', isActive: true };

export default function CreateUserPage() {
    const { user: currentUser } = useAuth();
    const canCreate = hasPermission(currentUser, 'create_users');
    const canEdit = hasPermission(currentUser, 'edit_users');
    const canDelete = hasPermission(currentUser, 'delete_users');

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useViewMode('echohire.users.view');

    // Role masters loaded from API (active only)
    const [roleMasters, setRoleMasters] = useState([]);

    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_EDIT);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient.getUsers();
            setUsers(res.data || []);
        } catch {
            toast.error('Failed to load team members.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Load active role masters for the dropdown
    const fetchRoleMasters = useCallback(async () => {
        try {
            const res = await apiClient.getMasters('role');
            const active = (res.data || []).filter(m => m.isActive);
            setRoleMasters(active);
            // Set default form role to first active master's slug (if any)
            if (active.length > 0) {
                setForm(prev => ({ ...prev, role: active[0].slug }));
            }
        } catch {
            // Non-critical — fall back to hardcoded roles
        }
    }, []);

    useEffect(() => { fetchUsers(); fetchRoleMasters(); }, [fetchUsers, fetchRoleMasters]);

    const filtered = users.filter(u => {
        if (roleFilter !== 'all' && u.role !== roleFilter) return false;
        if (statusFilter === 'active' && !u.isActive) return false;
        if (statusFilter === 'inactive' && u.isActive) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                u.firstName?.toLowerCase().includes(q) ||
                u.lastName?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const resetPage = () => setPage(1);
    useEffect(resetPage, [search, roleFilter, statusFilter]);

    // --- Create ---
    const handleCreate = async (e) => {
        e.preventDefault();
        // Client-side validation
        if (!form.firstName.trim()) { setFormError('First name is required.'); return; }
        if (!form.email.trim())     { setFormError('Email address is required.'); return; }
        if (!form.password)         { setFormError('Password is required.'); return; }
        if (form.password.length < 8) { setFormError('Password must be at least 8 characters long.'); return; }
        if (!form.role)             { setFormError('Please select a role for this user.'); return; }

        setFormLoading(true);
        setFormError('');
        try {
            await apiClient.register(form);
            toast.success(`${form.firstName} ${form.lastName || ''} added successfully.`);
            const resetRole = roleMasters.length > 0 ? roleMasters[0].slug : 'hr_manager';
            setForm({ ...EMPTY_FORM, role: resetRole });
            setShowPassword(false);
            setCreateOpen(false);
            fetchUsers();
        } catch (err) {
            const msg = err.message || 'Failed to create user.';
            setFormError(msg);
        } finally {
            setFormLoading(false);
        }
    };

    // --- Edit ---
    const openEdit = (user) => {
        setEditTarget(user);
        setEditForm({
            firstName: user.firstName,
            lastName: user.lastName || '',
            email: user.email || '',
            phone: user.phone || '',
            role: user.role,
            isActive: user.isActive ?? true,
        });
        setEditError('');
        setEditOpen(true);
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        if (!editTarget) return;
        if (!editForm.firstName.trim()) { setEditError('First name is required.'); return; }
        if (!editForm.email.trim()) { setEditError('Email address is required.'); return; }
        if (!/\S+@\S+\.\S+/.test(editForm.email.trim())) {
            setEditError('Please enter a valid email address.');
            return;
        }

        const isCandidate = editTarget.role === 'candidate';
        const payload = {
            firstName: editForm.firstName.trim(),
            lastName: editForm.lastName.trim(),
            email: editForm.email.trim(),
            phone: editForm.phone.trim(),
            isActive: editForm.isActive,
            ...(isCandidate ? {} : { role: editForm.role }),
        };

        setEditLoading(true);
        setEditError('');
        try {
            await apiClient.updateUser(editTarget._id, payload);
            toast.success(`${editForm.firstName} ${editForm.lastName || ''} updated successfully.`);
            setEditOpen(false);
            setEditTarget(null);
            fetchUsers();
        } catch (err) {
            setEditError(err.message || 'Failed to update user.');
        } finally {
            setEditLoading(false);
        }
    };

    // --- Delete ---
    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await apiClient.deleteUser(deleteTarget._id);
            toast.success(`${deleteTarget.firstName} ${deleteTarget.lastName} removed.`);
            setDeleteTarget(null);
            fetchUsers();
        } catch (err) {
            toast.error(err.message || 'Failed to delete user.');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Role count chips
    const roleCounts = BASE_ROLES.slice(1).reduce((acc, r) => {
        acc[r.value] = users.filter(u => u.role === r.value).length;
        return acc;
    }, {});

    return (
        <div className="space-y-6">
            <PageHeader
                title="Team Members"
                description={`${users.length} member${users.length !== 1 ? 's' : ''} in your organization`}
                actions={
                    canCreate && (
                        <Button onClick={() => setCreateOpen(true)} className="gap-2">
                            <Plus className="h-4 w-4" />
                            Add Member
                        </Button>
                    )
                }
            />

            {/* Role filter chips */}
            <div className="flex flex-wrap gap-2">
                {BASE_ROLES.map(r => (
                    <button
                        key={r.value}
                        type="button"
                        onClick={() => setRoleFilter(r.value)}
                        className={cn(
                            'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                            roleFilter === r.value
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-card text-muted-foreground hover:border-foreground/40 hover:text-foreground'
                        )}
                    >
                        {r.label}
                        {r.value !== 'all' && (
                            <span className={cn(
                                'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                                roleFilter === r.value ? 'bg-white/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
                            )}>
                                {roleCounts[r.value] || 0}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Search + status filter */}
            <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by name or email…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="h-10 pl-9 pr-9"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <div className="flex gap-2">
                    {['all', 'active', 'inactive'].map(s => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setStatusFilter(s)}
                            className={cn(
                                'rounded-lg border px-3 py-1.5 text-xs font-medium capitalize transition-colors',
                                statusFilter === s
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-border text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {s === 'all' ? 'All Status' : s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                    ))}
                </div>

                <ViewToggle value={viewMode} onChange={setViewMode} className="ml-auto" />
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {BASE_ROLES.slice(1, 5).map(r => {
                    const meta = ROLE_META[r.value];
                    const Icon = meta?.Icon || Users;
                    return (
                        <div key={r.value} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                            <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', meta?.avatar || 'bg-muted', 'bg-opacity-20')}>
                                <Icon className="h-4 w-4 text-foreground" />
                            </div>
                            <div>
                                <p className="text-lg font-bold leading-none text-foreground">{roleCounts[r.value] || 0}</p>
                                <p className="text-[11px] text-muted-foreground">{meta?.label}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* User grid */}
            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="h-44 animate-pulse rounded-xl border border-border bg-muted/40" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex h-52 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20">
                    <Users className="h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">No members match your filters.</p>
                    {(search || roleFilter !== 'all' || statusFilter !== 'all') && (
                        <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setRoleFilter('all'); setStatusFilter('all'); }}>
                            Clear filters
                        </Button>
                    )}
                </div>
            ) : viewMode === 'list' ? (
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                    <div className="hidden grid-cols-12 gap-2 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:grid">
                        <span className="col-span-4">Member</span>
                        <span className="col-span-3">Role</span>
                        <span className="col-span-2">Phone</span>
                        <span className="col-span-2">Status</span>
                        <span className="col-span-1" />
                    </div>
                    {paged.map(user => (
                        <UserRow
                            key={user._id}
                            user={user}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onEdit={openEdit}
                            onDelete={setDeleteTarget}
                            currentUserId={currentUser?._id}
                        />
                    ))}
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {paged.map(user => (
                        <UserCard
                            key={user._id}
                            user={user}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onEdit={openEdit}
                            onDelete={setDeleteTarget}
                            currentUserId={currentUser?._id}
                        />
                    ))}
                </div>
            )}

            <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
            />

            {/* Create User Dialog */}
            <Dialog open={createOpen} onOpenChange={o => {
                if (!formLoading) {
                    setCreateOpen(o);
                    if (!o) {
                        const resetRole = roleMasters.length > 0 ? roleMasters[0].slug : 'hr_manager';
                        setForm({ ...EMPTY_FORM, role: resetRole });
                        setFormError('');
                        setShowPassword(false);
                    }
                }
            }}>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Team Member</DialogTitle>
                        <DialogDescription>Create a new user account. The user can change their password after first login.</DialogDescription>
                    </DialogHeader>
                    <form id="create-user-form" onSubmit={handleCreate} className="space-y-4">
                        {/* Inline error banner */}
                        {formError && (
                            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
                                <XCircle className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
                                <p className="text-sm text-destructive leading-snug">{formError}</p>
                            </div>
                        )}

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="firstName">First name <span className="text-destructive">*</span></Label>
                                <Input
                                    id="firstName"
                                    value={form.firstName}
                                    onChange={e => { setForm({ ...form, firstName: e.target.value }); if (formError) setFormError(''); }}
                                    placeholder="e.g. Priya"
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lastName">Last name</Label>
                                <Input
                                    id="lastName"
                                    value={form.lastName}
                                    onChange={e => setForm({ ...form, lastName: e.target.value })}
                                    placeholder="e.g. Sharma"
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label htmlFor="email">Email address <span className="text-destructive">*</span></Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={form.email}
                                    onChange={e => { setForm({ ...form, email: e.target.value }); if (formError) setFormError(''); }}
                                    placeholder="e.g. priya@company.com"
                                    className="h-10"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label htmlFor="password">
                                    Password <span className="text-destructive">*</span>
                                    <span className="ml-1 font-normal text-muted-foreground">(min 8 characters)</span>
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={form.password}
                                        onChange={e => { setForm({ ...form, password: e.target.value }); if (formError) setFormError(''); }}
                                        placeholder="Minimum 8 characters"
                                        className="h-10 pr-10"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(v => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                        tabIndex={-1}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label htmlFor="phone">Phone <span className="font-normal text-muted-foreground">(optional)</span></Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={e => setForm({ ...form, phone: e.target.value })}
                                    placeholder="e.g. +91 98765 43210"
                                    className="h-10"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <NativeSelect
                                    id="role"
                                    label={
                                        <span>
                                            Role <span className="text-destructive">*</span>
                                            {roleMasters.length > 0 && (
                                                <span className="ml-1 font-normal text-muted-foreground text-xs">(from your company's role masters)</span>
                                            )}
                                        </span>
                                    }
                                    value={form.role}
                                    onChange={e => { setForm({ ...form, role: e.target.value }); if (formError) setFormError(''); }}
                                >
                                    {roleMasters.length > 0 ? (
                                        roleMasters.map(m => (
                                            <option key={m._id} value={m.slug}>{m.name}</option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="hr_manager">HR Manager</option>
                                            <option value="interviewer">Interviewer</option>
                                            <option value="candidate">Candidate</option>
                                            <option value="ml_engineer">ML Engineer</option>
                                        </>
                                    )}
                                </NativeSelect>
                                {roleMasters.length === 0 && (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        Add custom roles in{' '}
                                        <a href="/admin/masters" className="text-primary underline underline-offset-2">Interview Masters → Roles</a>
                                        {' '}to see them here.
                                    </p>
                                )}
                            </div>
                        </div>
                    </form>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={formLoading}>Cancel</Button>
                        <Button type="submit" form="create-user-form" disabled={formLoading} className="gap-2">
                            {formLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {formLoading ? 'Creating…' : 'Add Member'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={editOpen} onOpenChange={o => {
                if (!editLoading) {
                    setEditOpen(o);
                    if (!o) { setEditTarget(null); setEditError(''); }
                }
            }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editTarget?.role === 'candidate' ? 'Edit Candidate' : 'Edit Member'}</DialogTitle>
                        <DialogDescription>
                            Update details for {editTarget?.firstName} {editTarget?.lastName}
                        </DialogDescription>
                    </DialogHeader>
                    {editTarget && (
                        <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-3">
                            <UserAvatar user={editTarget} size="lg" />
                            <div>
                                <p className="font-semibold text-foreground">{editTarget.firstName} {editTarget.lastName}</p>
                                <RoleBadge role={editTarget.role} />
                            </div>
                        </div>
                    )}
                    <form id="edit-user-form" onSubmit={handleEdit} className="space-y-4">
                        {/* Inline error banner */}
                        {editError && (
                            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
                                <XCircle className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
                                <p className="text-sm text-destructive leading-snug">{editError}</p>
                            </div>
                        )}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="editFirst">First name <span className="text-destructive">*</span></Label>
                                <Input
                                    id="editFirst"
                                    value={editForm.firstName}
                                    onChange={e => { setEditForm({ ...editForm, firstName: e.target.value }); if (editError) setEditError(''); }}
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="editLast">Last name</Label>
                                <Input
                                    id="editLast"
                                    value={editForm.lastName}
                                    onChange={e => setEditForm({ ...editForm, lastName: e.target.value })}
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label htmlFor="editEmail">
                                    Email address <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    id="editEmail"
                                    type="email"
                                    value={editForm.email}
                                    onChange={e => { setEditForm({ ...editForm, email: e.target.value }); if (editError) setEditError(''); }}
                                    placeholder="e.g. candidate@email.com"
                                    className="h-10"
                                    autoComplete="off"
                                />
                                {editTarget?.role === 'candidate' && (
                                    <p className="text-[11px] text-muted-foreground">
                                        Used for login and interview notifications. Update here if the resume had a missing or incorrect email.
                                    </p>
                                )}
                            </div>
                            {editTarget?.role === 'candidate' && (
                                <div className="space-y-2 sm:col-span-2">
                                    <Label htmlFor="editPhone">Phone <span className="font-normal text-muted-foreground">(optional)</span></Label>
                                    <Input
                                        id="editPhone"
                                        type="tel"
                                        value={editForm.phone}
                                        onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                                        placeholder="e.g. +91 98765 43210"
                                        className="h-10"
                                    />
                                </div>
                            )}
                            {editTarget?.role !== 'candidate' && (
                            <div className="sm:col-span-2">
                                <NativeSelect
                                    label="Role"
                                    value={editForm.role}
                                    onChange={e => { setEditForm({ ...editForm, role: e.target.value }); if (editError) setEditError(''); }}
                                >
                                    {roleMasters.length > 0 ? (
                                        roleMasters.map(m => (
                                            <option key={m._id} value={m.slug}>{m.name}</option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="candidate">Candidate</option>
                                            <option value="interviewer">Interviewer</option>
                                            <option value="hr_manager">HR Manager</option>
                                            <option value="ml_engineer">ML Engineer</option>
                                        </>
                                    )}
                                    {currentUser?.role === 'super_admin' && <option value="admin">Admin</option>}
                                </NativeSelect>
                            </div>
                            )}
                            <div className="sm:col-span-2">
                                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-card p-3">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-foreground">Active account</p>
                                        <p className="text-xs text-muted-foreground">User can log in and access the platform</p>
                                    </div>
                                    <div
                                        role="checkbox"
                                        aria-checked={editForm.isActive}
                                        onClick={() => setEditForm({ ...editForm, isActive: !editForm.isActive })}
                                        className={cn(
                                            'relative h-5 w-9 cursor-pointer rounded-full transition-colors',
                                            editForm.isActive ? 'bg-primary' : 'bg-muted'
                                        )}
                                    >
                                        <span className={cn(
                                            'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                                            editForm.isActive ? 'translate-x-4' : 'translate-x-0.5'
                                        )} />
                                    </div>
                                </label>
                            </div>
                        </div>
                    </form>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
                        <Button type="submit" form="edit-user-form" disabled={editLoading} className="gap-2">
                            {editLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {editLoading ? 'Saving…' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm */}
            <ConfirmDialog
                open={Boolean(deleteTarget)}
                onOpenChange={o => { if (!o) setDeleteTarget(null); }}
                title="Remove team member"
                description={`Remove ${deleteTarget?.firstName} ${deleteTarget?.lastName} from the organization? This cannot be undone.`}
                confirmLabel="Remove"
                variant="destructive"
                loading={deleteLoading}
                onConfirm={handleDelete}
            />
        </div>
    );
}
